import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Alert, Box, Button, Chip, Dialog, DialogActions, DialogContent, DialogTitle, LinearProgress,
  MenuItem, Stack, Table, TableBody, TableCell, TableHead, TableRow, TextField, Typography, useMediaQuery, useTheme } from '@mui/material';
import PersonAddAltOutlined from '@mui/icons-material/PersonAddAltOutlined';
import api from '../axiosConfig';

const blank = { name: '', surname: '', company_name: '', email: '', role: 'viewer' };
const message = error => error?.response?.data?.message || 'Could not complete the request. Please try again.';
const date = value => new Date(value).toLocaleDateString(undefined, { day: 'numeric', month: 'short', year: 'numeric' });

export default function RegisterInvitations({ active, onUsage, onCreated, children }) {
  const mobile = useMediaQuery(useTheme().breakpoints.down('sm'));
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState(null);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(blank);
  const [busy, setBusy] = useState(false);
  const [formError, setFormError] = useState('');
  const [confirmation, setConfirmation] = useState(null);
  const requestId = useRef(0);
  const inFlight = useRef(false);
  const usageCallback = useRef(onUsage);
  usageCallback.current = onUsage;

  const refresh = useCallback(async () => {
    const version = ++requestId.current;
    setLoading(true);
    setError('');
    try {
      const response = await api.get('/api/register-invitations');
      if (version !== requestId.current) return;
      setData(response.data);
      usageCallback.current?.(response.data);
    } catch (err) {
      if (version === requestId.current) { setError(message(err)); setData(null); }
    } finally { if (version === requestId.current) setLoading(false); }
  }, []);

  useEffect(() => {
    if (active) refresh();
    else { setOpen(false); setData(null); setConfirmation(null); setError(''); setNotice(null); }
    return () => { requestId.current += 1; };
  }, [active, refresh]);

  const mutate = async (operation, creating = false) => {
    if (inFlight.current) return;
    inFlight.current = true;
    setBusy(true); setError(''); setFormError(''); setNotice(null);
    try {
      const response = await operation();
      setNotice({ severity: response.data.emailSent === false ? 'warning' : 'success', text: response.data.message });
      if (creating) { setOpen(false); onCreated?.(); }
      setConfirmation(null);
      await refresh();
    } catch (err) {
      if (creating) setFormError(message(err)); else setError(message(err));
      await refresh();
      if (!creating) setError(message(err));
    } finally { inFlight.current = false; setBusy(false); }
  };
  const full = data?.userLimit != null && data.occupiedSeats >= data.userLimit;
  if (!active) return children ? children({}) : null;

  const inviteButton = <Button variant="contained" startIcon={<PersonAddAltOutlined />} disabled={!data || loading || busy || full}
    onClick={() => { setForm(blank); setFormError(''); setOpen(true); }}>Invite user</Button>;
  const feedback = <>
    {loading && <LinearProgress aria-label="Loading invitations" />}
    {error && <Alert severity="error" action={<Button color="inherit" onClick={refresh}>Retry</Button>}>{error}</Alert>}
    {notice && <Alert severity={notice.severity} onClose={() => setNotice(null)}>{notice.text}</Alert>}
  </>;
  const invitationList = data && <Box sx={{ overflow: 'auto', flex: 1, minHeight: 0 }}>
    <Table size="small" aria-label="Register invitations" stickyHeader sx={{ tableLayout: mobile ? 'fixed' : 'auto', '& th': { bgcolor: 'grey.50' }, '& td': { py: 1.5 } }}>
      <TableHead><TableRow>{(mobile ? ['Invitation', 'Actions'] : ['Name / email', 'Role', 'Status', 'Expires', 'Actions']).map(label => <TableCell key={label} sx={mobile && label === 'Actions' ? { width: 130 } : {}}>{label}</TableCell>)}</TableRow></TableHead>
      <TableBody>
        {data.invitations.length === 0 && <TableRow><TableCell colSpan={mobile ? 2 : 5} sx={{ textAlign: 'center', py: '48px !important' }}>
          <Typography fontWeight={600}>No invitations yet</Typography>
          <Typography variant="body2" color="text.secondary">Invite a colleague to join this register. They will receive an email from EXREG.</Typography>
        </TableCell></TableRow>}
        {data.invitations.map(invite => {
          const status = <Chip size="small" variant="outlined"
            color={invite.status === 'pending' ? invite.email_failed ? 'error' : 'warning' : invite.status === 'accepted' ? 'success' : 'default'}
            label={invite.status === 'pending' ? invite.email_failed ? 'Email failed' : invite.email_sent_at ? 'Pending' : 'Delivery unconfirmed' : invite.status[0].toUpperCase() + invite.status.slice(1)} />;
          return <TableRow key={invite.id} hover>
            <TableCell sx={{ overflowWrap: 'anywhere' }}><Typography variant="body2" fontWeight={600}>{invite.name} {invite.surname}</Typography><Typography variant="caption" color="text.secondary">{invite.email}</Typography>
              {mobile && <Stack alignItems="flex-start" spacing={0.75} sx={{ mt: 1 }}>{status}<Typography variant="caption" sx={{ textTransform: 'capitalize' }}>{invite.role} · Expires {date(invite.expires_at)}</Typography></Stack>}
            </TableCell>
            {!mobile && <><TableCell sx={{ textTransform: 'capitalize' }}>{invite.role}</TableCell><TableCell>{status}</TableCell><TableCell sx={{ whiteSpace: 'nowrap' }}>{date(invite.expires_at)}</TableCell></>}
            <TableCell>{['pending', 'expired'].includes(invite.status) && <Stack direction={mobile ? 'column' : 'row'} alignItems="flex-start">
              <Button size="small" disabled={busy || loading} onClick={() => mutate(() => api.post(`/api/register-invitations/${invite.id}/resend`))}>Resend</Button>
              <Button size="small" color="inherit" disabled={busy || loading} onClick={() => setConfirmation({ type: 'cancel', invite })}>Cancel invitation</Button>
            </Stack>}</TableCell>
          </TableRow>;
        })}
      </TableBody>
    </Table>
    {data.canReleaseSeats && data.retainedUsers.length > 0 && <Box sx={{ p: 2, borderTop: '1px solid', borderColor: 'divider' }}>
      <Typography variant="subtitle2">Seats held by former members</Typography>
      {data.retainedUsers.map(user => <Stack key={user.user_id} direction="row" alignItems="center" gap={1} sx={{ py: 1 }}>
        <Typography variant="body2" sx={{ flex: 1 }}>{user.name} {user.surname} ({user.username})</Typography>
        <Button size="small" color="warning" disabled={busy} onClick={() => setConfirmation({ type: 'release', user })}>Release seat</Button>
      </Stack>)}
    </Box>}
  </Box>;
  return <>
    {children ? children({ data, loading, busy, full, refresh, inviteButton, invitationList, feedback }) : <Box>
      {inviteButton}{feedback}{invitationList}
    </Box>}
    <Dialog open={open} onClose={() => { if (!busy) setOpen(false); }} fullWidth maxWidth="sm" aria-labelledby="invite-user-title">
      <Box component="form" onSubmit={event => { event.preventDefault(); mutate(() => api.post('/api/register-invitations', Object.fromEntries(Object.entries(form).map(([key, value]) => [key, value.trim()]))), true); }}>
        <DialogTitle id="invite-user-title">Invite user</DialogTitle>
        <DialogContent dividers>
          <Typography variant="body2" sx={{ mb: 2 }}>Invite someone to <strong>{data?.databaseName}</strong>. They will receive an EXREG email to create their account or sign in and join.</Typography>
          {formError && <Alert severity="error" sx={{ mb: 2 }}>{formError}</Alert>}
          <Stack spacing={2}>
            {Object.entries({ name: 'First name', surname: 'Last name', company_name: 'Company', email: 'Company email' }).map(([key, label]) =>
              <TextField key={key} name={key} label={label} type={key === 'email' ? 'email' : 'text'} required fullWidth autoFocus={key === 'name'}
                inputProps={{ maxLength: 254 }} value={form[key]} disabled={busy} onChange={event => setForm(previous => ({ ...previous, [key]: event.target.value }))} />)}
            <TextField select label="Role" value={form.role} disabled={busy} onChange={event => setForm(previous => ({ ...previous, role: event.target.value }))}>
              {['viewer', 'inspector', 'editor'].map(role => <MenuItem key={role} value={role}>{role[0].toUpperCase() + role.slice(1)}</MenuItem>)}
            </TextField>
            <Typography variant="caption" color="text.secondary">The recipient chooses their own password. Invitations expire after 7 days.</Typography>
          </Stack>
        </DialogContent>
        <DialogActions><Button disabled={busy} onClick={() => setOpen(false)}>Cancel</Button><Button type="submit" variant="contained" disabled={busy || !data || full}>{busy ? 'Sending…' : 'Send invitation'}</Button></DialogActions>
      </Box>
    </Dialog>
    <Dialog open={!!confirmation} onClose={() => { if (!busy) setConfirmation(null); }} maxWidth="xs" fullWidth>
      <DialogTitle>{confirmation?.type === 'release' ? 'Release retained seat?' : 'Cancel invitation?'}</DialogTitle>
      <DialogContent>{confirmation?.type === 'release' ? 'This user has left the register. Releasing their seat makes it available for another user.' : `The invitation for ${confirmation?.invite.email} will stop working and its reservation will be released.`}</DialogContent>
      <DialogActions><Button disabled={busy} onClick={() => setConfirmation(null)}>Keep</Button><Button disabled={busy} color="warning" onClick={() => mutate(() => confirmation.type === 'release'
        ? api.delete(`/api/register-invitations/seats/${confirmation.user.user_id}`)
        : api.post(`/api/register-invitations/${confirmation.invite.id}/cancel`))}>Confirm</Button></DialogActions>
    </Dialog>
  </>;
}
